/**
 * useAIPrompts Hook
 *
 * Fetches AI prompt definitions from /api/settings/prompts.
 * Provides save and reset operations for per-prompt overrides.
 */

import { useState, useCallback, useEffect } from 'react';

export interface EffectivePrompt {
  id: string;
  label: string;
  description: string;
  systemPrompt: string;
  userPromptTemplate: string;
  isOverridden: boolean;
}

interface UseAIPromptsReturn {
  prompts: EffectivePrompt[];
  loading: boolean;
  error: string | null;
  /** ID of the prompt currently being saved/reset, or null */
  saving: string | null;
  saveOverride: (id: string, data: { systemPrompt?: string; userPromptTemplate?: string }) => Promise<void>;
  resetOverride: (id: string) => Promise<void>;
}

export function useAIPrompts(): UseAIPromptsReturn {
  const [prompts, setPrompts] = useState<EffectivePrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  const fetchPrompts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/settings/prompts');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json() as { data: EffectivePrompt[] };
      setPrompts(json.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load prompts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchPrompts();
  }, [fetchPrompts]);

  const saveOverride = useCallback(async (
    id: string,
    data: { systemPrompt?: string; userPromptTemplate?: string }
  ) => {
    setSaving(id);
    setError(null);
    try {
      const res = await fetch(`/api/settings/prompts/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json() as { data: EffectivePrompt };
      setPrompts((prev) => prev.map((p) => (p.id === id ? json.data : p)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
      throw err;
    } finally {
      setSaving(null);
    }
  }, []);

  const resetOverride = useCallback(async (id: string) => {
    setSaving(id);
    setError(null);
    try {
      const res = await fetch(`/api/settings/prompts/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json() as { data: EffectivePrompt };
      setPrompts((prev) => prev.map((p) => (p.id === id ? json.data : p)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset');
      throw err;
    } finally {
      setSaving(null);
    }
  }, []);

  return { prompts, loading, error, saving, saveOverride, resetOverride };
}
